import { useState, useCallback } from 'react';
import { evaluationService } from '../services/EvaluationService';
import { toast } from 'sonner';

interface CreateEvaluationPayload {
  name: string;
  dataset_id: number;
  deployment_id: string[];
  judge_model: string;
  prompt_template_id: string;
  prompt_template_version: string[];
  metrics: {
    [category: string]: Array<{ name: string }>;
  };
}

export const useEvaluation = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Create a new evaluation
   */
  const createEvaluation = useCallback(async (payload: CreateEvaluationPayload) => {
    setLoading(true);
    setError(null);
    try {
      const response = await evaluationService.createEvaluation(payload);
      toast.success('Evaluation created successfully');
      return response;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to create evaluation';
      setError(err);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get all evaluations
   */
  const getAllEvaluations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await evaluationService.getAllEvaluations();
      return response;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch evaluations';
      setError(err);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get evaluation by ID
   */
  const getEvaluationById = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await evaluationService.getEvaluationById(id);
      return response;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch evaluation details';
      setError(err);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get models list under evaluation
   */
  const getEvaluationModels = useCallback(async (evaluationId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await evaluationService.getEvaluationModels(evaluationId);
      return response;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch evaluation models';
      setError(err);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get prompt versions under selected model
   */
  const getModelVersions = useCallback(async (evaluationId: string, deploymentId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await evaluationService.getModelVersions(evaluationId, deploymentId);
      return response;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch model versions';
      setError(err);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get results of particular evaluation run
   */
  const getEvaluationRunResults = useCallback(async (runId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await evaluationService.getEvaluationRunResults(runId);
      return response;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch run results';
      setError(err);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    createEvaluation,
    getAllEvaluations,
    getEvaluationById,
    getEvaluationModels,
    getModelVersions,
    getEvaluationRunResults,
  };
};
